// @ts-strict-ignore
import { FetchResult } from "@apollo/client";
import {
  getAttributesAfterFileAttributesUpdate,
  mergeFileUploadErrors,
} from "@dashboard/attributes/utils/data";
import {
  handleUploadMultipleFiles,
  prepareAttributesInput,
} from "@dashboard/attributes/utils/handlers";
import { ChannelData } from "@dashboard/channels/utils";
import {
  AttributeErrorFragment,
  FileUploadMutation,
  FileUploadMutationVariables,
  ProductChannelListingErrorFragment,
  ProductChannelListingUpdateMutation,
  ProductChannelListingUpdateMutationVariables,
  ProductCreateMutation,
  ProductCreateMutationVariables,
  ProductDeleteMutation,
  ProductDeleteMutationVariables,
  ProductErrorFragment,
  ProductTypeQuery,
  ProductVariantChannelListingUpdateMutation,
  ProductVariantChannelListingUpdateMutationVariables,
  UploadErrorFragment,
  VariantCreateMutation,
  VariantCreateMutationVariables,
} from "@dashboard/graphql";
import { weight } from "@dashboard/misc";
import { ProductCreateData } from "@dashboard/products/components/ProductCreatePage/form";
import { getAvailabilityVariables } from "@dashboard/products/utils/handlers";
import { getParsedDataForJsonStringField } from "@dashboard/utils/richText/misc";

const getChannelsVariables = (productId: string, channels: ChannelData[]) => ({
  variables: {
    id: productId,
    input: {
      updateChannels: getAvailabilityVariables(channels),
    },
  },
});
const getSimpleProductVariables = (formData: ProductCreateData, productId: string) => ({
  input: {
    attributes: [],
    product: productId,
    sku: formData.sku,
    stocks: formData.stocks?.map(stock => ({
      quantity: parseInt(stock.value, 10),
      warehouse: stock.id,
    })),
    preorder: formData.isPreorder
      ? {
          globalThreshold: formData.globalThreshold ? parseInt(formData.globalThreshold, 10) : null,
          endDate: formData.preorderEndDateTime || null,
        }
      : null,
    trackInventory: formData.trackInventory,
  },
});

/**
 * "225/45R17"‑style formatter.
 * Returns `undefined` if any piece is missing.
 */
const buildSize = (w?: string, ar?: string, rd?: string) =>
  w && ar && rd ? `${w}/${ar}R${rd}` : undefined;

/**
 * Replace the `Size` attribute’s value with the generated string, if any.
 * Returns a **new** array (keeps everything else intact).
 */
const calculatedSize = (
  attrs: ProductCreateData["attributes"],
): string | undefined => {
  const w  = findAttr(attrs, "Section Width")?.value?.[0] as string | undefined;
  const ar = findAttr(attrs, "Aspect Ratio")?.value?.[0] as string | undefined;
  const rd = findAttr(attrs, "Rim Diameter")?.value?.[0] as string | undefined;

  return buildSize(w, ar, rd);
};

/** Type alias just to keep the helpers readable */
type Attr = ProductCreateData["attributes"][number];

/** Case‑insensitive finder for an attribute by its label. */
const findAttr = (attrs: Attr[], label: string): Attr | undefined =>
  attrs.find(a => a.label?.toLowerCase() === label.toLowerCase());

/**
 * Convert the stored slug (e.g. "eagle-f1") into its human‑readable name
 * by looking it up in `attribute.data.values`.
 * Falls back to the slug itself if we can’t find a match or `data` is missing.
 */
const slugToName = (attr?: Attr): string | undefined => {
  const slug = attr?.value?.[0] as string | undefined;
  if (!slug) {
    return undefined;
  }

  // In the form payload `data` is preserved, so we can do the lookup:
  const match = (attr as any).data?.values?.find(
    (v: { slug: string }) => v.slug === slug,
  );

  return match?.name ?? slug; // fallback to slug if name not found
};

/**
 * Build the product name in the requested order, resolving each slug
 * to its user‑friendly `name`.
 */
const buildName = (attrs: Attr[]): string => {
  const sizeAttr  = calculatedSize(attrs)           
  const treadAttr = findAttr(attrs, "Tread Depth");

  const tread = slugToName(treadAttr);
  const parts = [
    sizeAttr,                    
    slugToName(findAttr(attrs, "Brand")),              
    slugToName(findAttr(attrs, "Model")),             
    slugToName(findAttr(attrs, "Load Index")) && slugToName(findAttr(attrs, "Speed Index")) ? `${slugToName(findAttr(attrs, "Load Index"))}${slugToName(findAttr(attrs, "Speed Index"))}` : undefined,
    slugToName(findAttr(attrs, "Load Range")),                                        
  ].filter(Boolean);

  return (
    parts.join(" ") +
    (tread ? ` - ${tread}${tread.includes("/") ? "" : "/32"}` : "")
  );
};

export function createHandler(
  productType: ProductTypeQuery["productType"],
  uploadFile: (variables: FileUploadMutationVariables) => Promise<FetchResult<FileUploadMutation>>,
  productCreate: (
    variables: ProductCreateMutationVariables,
  ) => Promise<FetchResult<ProductCreateMutation>>,
  productVariantCreate: (
    variables: VariantCreateMutationVariables,
  ) => Promise<FetchResult<VariantCreateMutation>>,
  updateChannels: (options: {
    variables: ProductChannelListingUpdateMutationVariables;
  }) => Promise<FetchResult<ProductChannelListingUpdateMutation>>,
  updateVariantChannels: (options: {
    variables: ProductVariantChannelListingUpdateMutationVariables;
  }) => Promise<FetchResult<ProductVariantChannelListingUpdateMutation>>,
  productDelete: (options: {
    variables: ProductDeleteMutationVariables;
  }) => Promise<FetchResult<ProductDeleteMutation>>,
) {
  return async (formData: ProductCreateData) => {
    let errors: Array<
      | AttributeErrorFragment
      | UploadErrorFragment
      | ProductErrorFragment
      | ProductChannelListingErrorFragment
    > = [];

    const uploadFilesResult = await handleUploadMultipleFiles(
      formData.attributesWithNewFileValue,
      uploadFile,
    );

    errors = [...errors, ...mergeFileUploadErrors(uploadFilesResult)];

    const updatedFileAttributes = getAttributesAfterFileAttributesUpdate(
      formData.attributesWithNewFileValue,
      uploadFilesResult,
    );

    // const resolvedAttributes = withCalculatedSize(formData.attributes);
    const generatedName = buildName(formData.attributes);
    const productVariables: ProductCreateMutationVariables = {
      input: {
        attributes: prepareAttributesInput({
          attributes: formData.attributes,
          prevAttributes: null,
          updatedFileAttributes,
        }),
        category: formData.category,
        collections: formData.collections.map(collection => collection.value),
        description: getParsedDataForJsonStringField(formData.description),
        name: generatedName || formData.name,
        productType: formData.productType?.id,
        rating: formData.rating,
        seo: {
          description: formData.seoDescription,
          title: formData.seoTitle,
        },
        slug: formData.slug,
        taxClass: formData.taxClassId,
        weight: weight(formData.weight),
      },
    };
    const result = await productCreate(productVariables);
    const productErrors = result?.data?.productCreate?.errors ?? [];

    errors = [...errors, ...productErrors];

    const hasVariants = productType?.hasVariants;
    const productId = result?.data?.productCreate?.product?.id;

    if (!productId) {
      return { errors };
    }

    if (!hasVariants) {
      const result = await Promise.all([
        updateChannels(getChannelsVariables(productId, formData.channelListings)),
        productVariantCreate(getSimpleProductVariables(formData, productId)),
      ]);
      const channelErrors = result[0].data?.productChannelListingUpdate?.errors || [];
      const variantErrors = result[1].data?.productVariantCreate?.errors || [];

      errors = [...errors, ...channelErrors, ...variantErrors];

      const variantId = result[1].data.productVariantCreate.productVariant?.id;

      if (variantErrors.length === 0 && variantId) {
        updateVariantChannels({
          variables: {
            id: variantId,
            input: formData.channelListings.map(listing => ({
              channelId: listing.id,
              costPrice: listing.costPrice || null,
              price: listing.price,
            })),
          },
        });
      }
    } else {
      const result = await updateChannels(
        getChannelsVariables(productId, formData.channelListings),
      );
      const channelErrors = result.data?.productChannelListingUpdate?.errors || [];

      errors = [...errors, ...channelErrors];
    }

    /*
     INFO: This is a stop-gap solution, where we delete products that didn't meet all required data in the create form
     A more robust solution would require merging create and update form into one to persist form state across redirects
    */
    if (productId && errors.length > 0) {
      await productDelete({ variables: { id: productId } });

      return { errors };
    }

    return { id: productId || null, errors };
  };
}
